import React, { useState } from "react";
import {
  Modal,
  ModalContentWrapper,
  Button,
  Spinner,
  Input,
  TextArea,
} from "components";
import { DistributorService } from "../../services/DistributorService";
import { useDistributorStore } from "../../store/distributorStore";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { email?: string; feedback: string }) => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ feedback?: string }>({});
  const { distributorId } = useDistributorStore();
  const distributorService = new DistributorService();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setErrors({});

    if (!feedback.trim()) {
      setErrors({ feedback: "Feedback is required" });
      return;
    }

    setIsSubmitting(true);

    try {
      const feedbackData = {
        email: email.trim() || undefined,
        feedback: feedback.trim(),
        title: "Feedback",
        distributorId: distributorId,
      };
      await distributorService.sendFeedback(feedbackData);

      setEmail("");
      setFeedback("");
      setErrors({});
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail("");
      setFeedback("");
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Send Feedback">
      <ModalContentWrapper>
        <form onSubmit={handleSubmit} className="space-y-4 w-[600px]">
          <Input
            label="Email (Optional)"
            type="email"
            placeholder="your.email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="default"
            description="We'll use this to follow up on your feedback if needed"
          />

          <TextArea
            label="Feedback"
            placeholder="Please share your thoughts, suggestions, or report any issues..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            variant="default"
            error={errors.feedback}
            required
            rows={4}
            description="Your feedback helps us improve the platform"
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !feedback.trim()}>
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2" />
                  Sending...
                </>
              ) : (
                "Send Feedback"
              )}
            </Button>
          </div>
        </form>
      </ModalContentWrapper>
    </Modal>
  );
};
